import { OtpFields } from './OtpFields';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../app/routes';
import { completeLogin } from '../../app/sessionSlice';
import { useAppDispatch } from '../../app/store';
import { authApi } from '../../core/api/client';
import { PlatformApiError } from '../../core/http/errors';
import { AuthFrame, GoogleButton } from './AuthFrame';

/**
 * Public sign-up. The account joins the shared COMMUNITY organization with the COMMUNITY_MEMBER
 * role (PlatformDB script 027): the person can post and comment on the landing feed and sees
 * their own activity, but gets no application menus until a business adds them as a member.
 */
export function RegisterPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', userName: '', password: '', confirm: '' });
  const [verification, setVerification] = useState({ email: '', challengeId: '', code: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (form.password.length < 10) { setError('Password must contain at least 10 characters.'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    if (verification.email !== form.email || !verification.challengeId || !/^\d{6}$/.test(verification.code)) { setError('Request and enter the email verification code.'); return; }
    setBusy(true);
    try {
      const { organizationId } = await authApi.register({
        challengeId: verification.challengeId, code: verification.code,
        firstName: form.firstName, lastName: form.lastName, email: form.email,
        userName: form.userName, password: form.password,
      });
      await dispatch(completeLogin({
        login: form.email, password: form.password, organizationId, productCode: 'PLATFORM',
      })).unwrap();
      navigate(ROUTES.home, { replace: true });
    } catch (caught) {
      setError(caught instanceof PlatformApiError || caught instanceof Error ? caught.message : 'Sign up failed.');
      setBusy(false);
    }
  };

  return (
    <AuthFrame title={t('auth.register.title', 'Create your account')}>
      <h1>{t('auth.register.title', 'Create your account')}</h1>
      <p className="lead" style={{ marginBottom: 18 }}>{t('auth.register.lead', 'Book, order, apply for jobs and keep your receipts in one place.')}</p>

      <GoogleButton label={t('auth.googleSignUp', 'Sign up with Google')} />
      <div className="ax-divider">{t('auth.or', 'or')}</div>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }}>
          <TextField label="First name" value={form.firstName} onChange={set('firstName')} fullWidth required margin="normal" autoComplete="given-name" autoFocus />
          <TextField label="Last name" value={form.lastName} onChange={set('lastName')} fullWidth required margin="normal" autoComplete="family-name" />
        </Stack>
        <TextField label="Email" type="email" value={form.email} onChange={set('email')} fullWidth required margin="normal" autoComplete="email" />
        <OtpFields email={form.email} purpose="Registration" busy={busy} onChange={setVerification} />
        <TextField label="Username" value={form.userName} onChange={set('userName')} fullWidth required margin="normal" autoComplete="username" />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }}>
          <TextField label="Password" type="password" value={form.password} onChange={set('password')} fullWidth required margin="normal" autoComplete="new-password" helperText="At least 10 characters" />
          <TextField label="Confirm password" type="password" value={form.confirm} onChange={set('confirm')} fullWidth required margin="normal" autoComplete="new-password" />
        </Stack>
        <button type="submit" className="ax-primary" disabled={busy} style={{ marginTop: 16 }}>
          {busy ? t('auth.register.creating', 'Creating account…') : t('auth.register.submit', 'Create account')}
        </button>
      </Box>

      <p className="ax-foot">
        {t('auth.register.haveAccount', 'Already have an account?')} <RouterLink to={ROUTES.login}>{t('auth.login.submit', 'Sign in')}</RouterLink>
      </p>
    </AuthFrame>
  );
}
